import React, { useState, useRef, useEffect } from 'react';
import { X, Calendar, User, MessageSquare, Paperclip, CheckCircle2, Trash2, Download, Upload, Send, ZoomIn } from 'lucide-react';
import { useTask } from '../../contexts/TaskContext';
import { useAuth } from '../../contexts/AuthContext';
import TaskStatusBadge from './TaskStatusBadge';
import toast from 'react-hot-toast';
import config from '../../config/config';

const TaskDetailModal = ({ task, onClose }) => {
  const {
    completeTask,
    deleteTask,
    addComment,
    deleteComment,
    addAttachment,
    deleteAttachment,
    downloadAttachment
  } = useTask();

  const [commentText, setCommentText] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [deletingTask, setDeletingTask] = useState(false);
  const [completingTask, setCompletingTask] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const fileInputRef = useRef(null);
  const commentInputRef = useRef(null);

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'completed';

  // Yorum ekle
  const handleAddComment = async () => {
    if (!commentText.trim()) {
      toast.error('Yorum boş olamaz');
      return;
    }

    try {
      setAddingComment(true);
      await addComment(task.id, commentText.trim());
      setCommentText('');
      toast.success('Yorum eklendi');
    } catch (error) {
      console.error('Yorum ekleme hatası:', error);
      toast.error(error.response?.data?.message || 'Yorum eklenemedi');
    } finally {
      setAddingComment(false);
    }
  };

  // Dosya yükle
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      console.log('Dosya seçilmedi');
      return;
    }

    if (!task || !task.id) {
      console.error('Task veya task.id bulunamadı:', task);
      toast.error('Görev bilgisi bulunamadı');
      return;
    }

    console.log('Dosya yükleniyor:', file.name, 'Task ID:', task.id);

    try {
      setUploadingFile(true);
      await addAttachment(task.id, file);
      toast.success('Dosya eklendi');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Dosya yükleme hatası:', error);
      console.error('Hata detayları:', error.response?.data);
      toast.error(error.response?.data?.message || 'Dosya yüklenemedi');
    } finally {
      setUploadingFile(false);
    }
  };

  // Görevi tamamla
  const handleCompleteTask = async () => {
    if (task.status === 'completed') {
      toast.error('Görev zaten tamamlanmış');
      return;
    }

    if (!window.confirm('Bu görevi tamamlandı olarak işaretlemek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      setCompletingTask(true);
      await completeTask(task.id);
      toast.success('Görev tamamlandı');
    } catch (error) {
      console.error('Görev tamamlama hatası:', error);
      toast.error(error.response?.data?.message || 'Görev tamamlanamadı');
    } finally {
      setCompletingTask(false);
    }
  };

  // Görevi sil
  const handleDeleteTask = async () => {
    if (!window.confirm('Bu görevi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.')) {
      return;
    }

    try {
      setDeletingTask(true);
      await deleteTask(task.id);
      toast.success('Görev silindi');
      onClose();
    } catch (error) {
      console.error('Görev silme hatası:', error);
      toast.error(error.response?.data?.message || 'Görev silinemedi');
    } finally {
      setDeletingTask(false);
    }
  };

  // Yorum sil
  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Bu yorumu silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await deleteComment(task.id, commentId);
      toast.success('Yorum silindi');
    } catch (error) {
      console.error('Yorum silme hatası:', error);
      toast.error(error.response?.data?.message || 'Yorum silinemedi');
    }
  };

  // Dosya sil
  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Bu dosyayı silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await deleteAttachment(task.id, attachmentId);
      toast.success('Dosya silindi');
    } catch (error) {
      console.error('Dosya silme hatası:', error);
      toast.error(error.response?.data?.message || 'Dosya silinemedi');
    }
  };

  // Dosya indir
  const handleDownloadAttachment = async (attachmentId, fileName) => {
    try {
      await downloadAttachment(task.id, attachmentId, fileName);
    } catch (error) {
      console.error('Dosya indirme hatası:', error);
      toast.error('Dosya indirilemedi');
    }
  };

  // Resim dosyası mı kontrol et
  const isImageFile = (attachment) => {
    if (!attachment) return false;
    const mimeType = attachment.mimeType || '';
    const fileName = attachment.fileName || '';
    
    // MIME type kontrolü
    if (mimeType && mimeType.startsWith('image/')) {
      return true;
    }
    
    // Dosya uzantısı kontrolü
    const imageExtensions = /\.(jpg|jpeg|png|gif|webp|bmp|svg|ico)$/i;
    if (fileName && imageExtensions.test(fileName)) {
      return true;
    }
    
    return false;
  };

  // Resim URL'i oluştur
  const getImageUrl = (attachment) => {
    if (!attachment || !attachment.filePath) {
      return null;
    }
    
    let filePath = attachment.filePath.trim();
    
    // Backend'den gelen filePath /uploads/tasks/... formatında olmalı
    // Eğer / ile başlamıyorsa ekle
    if (!filePath.startsWith('/')) {
      filePath = `/${filePath}`;
    }
    
    // Çift slash'ı önle
    filePath = filePath.replace(/\/+/g, '/');
    
    // uploadsUrl zaten domain içeriyor (örn: https://jansoft.online)
    // filePath zaten /uploads/tasks/... formatında
    const imageUrl = `${config.uploadsUrl}${filePath}`;
    return imageUrl;
  };

  // Resmi büyüt
  const handleImageClick = (attachment) => {
    if (isImageFile(attachment)) {
      setSelectedImage(getImageUrl(attachment));
    }
  };

  // ESC tuşu ile kapat
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const { user } = useAuth();
  const currentUserId = user?.id;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"
          onClick={(e) => {
            // Sadece backdrop'a tıklanırsa kapat
            if (e.target === e.currentTarget) {
              onClose();
            }
          }}
        />

        {/* Modal Panel */}
        <div
          className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-4 sm:align-middle sm:max-w-lg sm:w-full"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-3 py-2.5 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-1">
                  <TaskStatusBadge status={task.status} />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {task.title}
                  </h3>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  {task.dueDate && (
                    <div className={`flex items-center gap-1 ${isOverdue ? 'text-red-600 dark:text-red-400 font-medium' : ''}`}>
                      <Calendar className="w-3 h-3" />
                      <span>{formatDateOnly(task.dueDate)}</span>
                      {isOverdue && <span className="text-xs">(Gecikmiş)</span>}
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    <span className="truncate">
                      {task.assignedTo?.username || 'Bilinmeyen'}
                    </span>
                  </div>
                  {task.assignedBy && (
                    <div className="flex items-center gap-1">
                      <span className="text-xs">Atayan:</span>
                      <span className="truncate">
                        {task.assignedBy.username || 'Bilinmeyen'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                className="flex-shrink-0 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 p-0.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-3 py-2.5 max-h-[calc(100vh-220px)] overflow-y-auto">
            {/* Açıklama */}
            {task.description && (
              <div className="mb-3">
                <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Açıklama
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap leading-relaxed">
                  {task.description}
                </p>
              </div>
            )}

            {/* Yorumlar */}
            <div className="mb-3">
              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5" />
                Yorumlar ({task.comments?.length || 0})
              </h4>

              {/* Yorum Listesi */}
              <div className="space-y-1.5 mb-2">
                {task.comments && task.comments.length > 0 ? (
                  task.comments.map((comment) => (
                    <div
                      key={comment.id}
                      className="p-1.5 bg-gray-50 dark:bg-gray-700 rounded-md"
                    >
                      <div className="flex items-start justify-between gap-1.5">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <span className="text-xs font-medium text-gray-900 dark:text-white">
                              {comment.username || 'Bilinmeyen'}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {formatDate(comment.createdAt)}
                            </span>
                          </div>
                          <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                            {comment.comment}
                          </p>
                        </div>
                        {comment.userId === currentUserId && (
                          <button
                            onClick={() => handleDeleteComment(comment.id)}
                            className="flex-shrink-0 text-red-500 hover:text-red-700 p-0.5"
                            title="Yorumu sil"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-2">
                    Henüz yorum yok
                  </p>
                )}
              </div>

              {/* Yorum Ekleme */}
              <div className="flex gap-1">
                <textarea
                  ref={commentInputRef}
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.ctrlKey) {
                      handleAddComment();
                    }
                  }}
                  placeholder="Yorum yazın... (Ctrl+Enter)"
                  rows={2}
                  className="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                />
                <button
                  onClick={handleAddComment}
                  disabled={addingComment || !commentText.trim()}
                  className="px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
                >
                  <Send className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Dosyalar */}
            <div>
              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 flex items-center gap-1.5">
                <Paperclip className="w-3.5 h-3.5" />
                Dosyalar ({task.attachments?.length || 0})
              </h4>

              {/* Dosya Listesi */}
              <div className="space-y-2 mb-2">
                {task.attachments && task.attachments.length > 0 ? (
                  task.attachments.map((attachment) => {
                    const isImage = isImageFile(attachment);
                    const imageUrl = isImage ? getImageUrl(attachment) : null;
                    
                    return (
                      <div
                        key={attachment.id}
                        className={`p-2 bg-gray-50 dark:bg-gray-700 rounded-md ${isImage ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors' : ''}`}
                        onClick={() => isImage && handleImageClick(attachment)}
                      >
                        {isImage && imageUrl ? (
                          <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-2">
                            <div className="flex-shrink-0 w-full sm:w-auto relative">
                              <img
                                src={imageUrl}
                                alt={attachment.fileName}
                                className="w-full sm:w-20 sm:h-20 h-48 sm:h-auto object-cover rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleImageClick(attachment);
                                }}
                                onError={(e) => {
                                  // Resim yüklenemezse placeholder göster
                                  const img = e.target;
                                  img.style.display = 'none';
                                  const parent = img.parentElement;
                                  const placeholder = parent.querySelector('.image-placeholder');
                                  if (placeholder) {
                                    placeholder.classList.remove('hidden');
                                    placeholder.classList.add('flex');
                                  }
                                }}
                                onLoad={(e) => {
                                  // Resim yüklendiğinde görünürlüğü garanti et
                                  const img = e.target;
                                  img.style.display = 'block';
                                  img.style.visibility = 'visible';
                                  img.style.opacity = '1';
                                }}
                                loading="lazy"
                                decoding="async"
                                style={{ 
                                  WebkitUserSelect: 'none', 
                                  userSelect: 'none',
                                  WebkitTouchCallout: 'none',
                                  touchAction: 'manipulation',
                                  display: 'block',
                                  visibility: 'visible',
                                  opacity: '1'
                                }}
                                draggable="false"
                              />
                              <div className="image-placeholder w-full sm:w-20 sm:h-20 h-48 sm:h-auto hidden flex-col items-center justify-center bg-gray-200 dark:bg-gray-600 rounded border border-gray-300 dark:border-gray-600 absolute inset-0">
                                <Paperclip className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400 mb-1" />
                                <span className="text-xs text-gray-500 dark:text-gray-400 text-center px-2">Önizleme yüklenemedi</span>
                              </div>
                            </div>
                            <div className="flex-1 min-w-0 w-full sm:w-auto">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-1.5 mb-2 sm:mb-1">
                                <span className="text-xs sm:text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                                  {attachment.fileName}
                                </span>
                                {attachment.fileSize && (
                                  <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                                    ({(attachment.fileSize / 1024).toFixed(1)} KB)
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-2 sm:gap-1.5">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleImageClick(attachment);
                                  }}
                                  className="text-blue-500 hover:text-blue-700 active:bg-blue-50 dark:active:bg-blue-900/20 px-2 py-1.5 sm:p-0.5 rounded sm:rounded-none flex items-center gap-1.5 sm:gap-1 text-xs sm:text-xs touch-manipulation"
                                  title="Büyüt"
                                >
                                  <ZoomIn className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                                  <span className="text-xs sm:text-xs">Büyüt</span>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownloadAttachment(attachment.id, attachment.fileName);
                                  }}
                                  className="text-blue-500 hover:text-blue-700 active:bg-blue-50 dark:active:bg-blue-900/20 px-2 py-1.5 sm:p-0.5 rounded sm:rounded-none flex items-center gap-1.5 sm:gap-1 text-xs sm:text-xs touch-manipulation"
                                  title="İndir"
                                >
                                  <Download className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                                  <span className="text-xs sm:text-xs">İndir</span>
                                </button>
                                {attachment.userId === currentUserId && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteAttachment(attachment.id);
                                    }}
                                    className="text-red-500 hover:text-red-700 active:bg-red-50 dark:active:bg-red-900/20 px-2 py-1.5 sm:p-0.5 rounded sm:rounded-none flex items-center gap-1.5 sm:gap-1 text-xs sm:text-xs touch-manipulation"
                                    title="Sil"
                                  >
                                    <Trash2 className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                                    <span className="text-xs sm:text-xs">Sil</span>
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5 flex-1 min-w-0">
                              <Paperclip className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                              <span className="text-xs text-gray-700 dark:text-gray-300 truncate">
                                {attachment.fileName}
                              </span>
                              {attachment.fileSize && (
                                <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                                  ({(attachment.fileSize / 1024).toFixed(1)} KB)
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => handleDownloadAttachment(attachment.id, attachment.fileName)}
                                className="text-blue-500 hover:text-blue-700 p-0.5"
                                title="İndir"
                              >
                                <Download className="w-3.5 h-3.5" />
                              </button>
                              {attachment.userId === currentUserId && (
                                <button
                                  onClick={() => handleDeleteAttachment(attachment.id)}
                                  className="text-red-500 hover:text-red-700 p-0.5"
                                  title="Sil"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-2">
                    Henüz dosya yok
                  </p>
                )}
              </div>

              {/* Dosya Yükleme */}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileUpload}
                  disabled={uploadingFile || !task || !task.id}
                  className="hidden"
                  accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/csv"
                />
                <button
                  onClick={() => {
                    if (!task || !task.id) {
                      toast.error('Görev bilgisi bulunamadı');
                      return;
                    }
                    if (fileInputRef.current) {
                      fileInputRef.current.click();
                    }
                  }}
                  disabled={uploadingFile || !task || !task.id}
                  className="w-full px-2.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 transition-colors"
                >
                  <Upload className="w-3.5 h-3.5" />
                  {uploadingFile ? 'Yükleniyor...' : 'Dosya Ekle'}
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center gap-2">
            <div className="flex gap-1">
              {task.status !== 'completed' && task.assignedToId === currentUserId && (
                <button
                  onClick={handleCompleteTask}
                  disabled={completingTask}
                  className="px-2.5 py-1 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 transition-colors"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {completingTask ? 'Tamamlanıyor...' : 'Tamamla'}
                </button>
              )}
              {task.assignedById === currentUserId && (
                <button
                  onClick={handleDeleteTask}
                  disabled={deletingTask}
                  className="px-2.5 py-1 text-xs font-medium text-red-600 bg-red-50 dark:bg-red-900/20 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {deletingTask ? 'Siliniyor...' : 'Sil'}
                </button>
              )}
            </div>
            <button
              onClick={onClose}
              className="px-2.5 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              Kapat
            </button>
          </div>
        </div>
      </div>

      {/* Resim Büyütme Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-[60] bg-black bg-opacity-95 flex items-center justify-center p-2 sm:p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative w-full h-full flex items-center justify-center">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-2 sm:top-4 right-2 sm:right-4 text-white hover:text-gray-300 active:text-gray-400 p-2 sm:p-3 bg-black/50 rounded-full backdrop-blur-sm touch-manipulation z-10"
              aria-label="Kapat"
            >
              <X className="w-6 h-6 sm:w-8 sm:h-8" />
            </button>
            <img
              src={selectedImage}
              alt="Büyütülmüş görüntü"
              className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
              style={{ touchAction: 'none' }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskDetailModal;

